import React from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import SprintMetricsStripSimple from '../components/Sprints/SprintMetricsStripSimple';
import SprintKanbanBoard from '../components/Sprints/SprintKanbanBoard';
import SprintCommentsSection from '../components/Sprints/SprintCommentsSection';
import SprintEmptyState from '../components/Sprints/SprintEmptyState';
import AddFromBacklogDrawer from '../components/Sprints/AddFromBacklogDrawer';
import TaskDetailModal from '../components/AdvisorMap/TaskDetailModal';
import AssessmentPromptEmptyState from '../components/Succession/AssessmentPromptEmptyState';
import { shouldShowAssessmentPrompt } from '../utils/unifiedIntake';
import SingleColumnTemplate from '../../../ui/layouts/SingleColumnTemplate';
import { Box } from '@material-ui/core';

const SPRINT_QUERY = gql`
  query Sprint($id: ID!) {
    sprint(id: $id) {
      id
      name
      objectiveText
      startOn
      endOn
      status
      metricsSnapshot: metrics_snapshot
      commentCount: comment_count
      unreadCommentCount: unread_comment_count
      items {
        id
        title
        description
        status
        priority
        dueAt
        assigneeUserId
        domain
        cepaCategory
        taskTemplate { id key title description cepaCategory deadlineInDays domain }
        commentCount: comment_count
        unreadCommentCount: unread_comment_count
        documents {
          id
          name
          download_url
          created_at
        }
      }
    }
  }
`;

const GET_CURRENT_USER = gql`
  query GetCurrentUserForIntake {
    getCurrentUser {
      id
      triggering_events { event_type completed_at }
    }
  }
`;

const ACTION_PLAN_QUERY = gql`
  query ActionPlan {
    actionPlan {
      id
      items { id title status priority dueAt assigneeUserId domain cepaCategory }
    }
  }
`;

const UPDATE_ITEM = gql`
  mutation UpdateActionPlanItem(
    $id: ID!
    $status: ActionPlanItemStatusEnum
    $priority: ActionPlanItemPriorityEnum
    $dueAt: ISO8601DateTime
    $assigneeUserId: ID
    $pipelineCardId: ID
    $title: String
    $description: String
  ) {
    update_action_plan_item(
      id: $id
      status: $status
      priority: $priority
      dueAt: $dueAt
      assigneeUserId: $assigneeUserId
      pipelineCardId: $pipelineCardId
      title: $title
      description: $description
    ) {
      id
      status
      priority
      dueAt
    }
  }
`;

const ADD_ITEMS_TO_SPRINT = gql`
  mutation AddItemsToSprint($sprint_id: ID!, $item_ids: [ID!]!) {
    add_items_to_sprint(sprint_id: $sprint_id, item_ids: $item_ids) {
      id
      items { id status }
      metrics_snapshot
    }
  }
`;

const SprintDetailContainer = ({ sprintId }) => {
  const { data, loading, error, refetch } = useQuery(SPRINT_QUERY, { variables: { id: sprintId }, skip: !sprintId });
  const { data: cuData } = useQuery(GET_CURRENT_USER);
  const { data: apData } = useQuery(ACTION_PLAN_QUERY);
  const [updateItem] = useMutation(UPDATE_ITEM);
  const [addItems] = useMutation(ADD_ITEMS_TO_SPRINT);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [taskModalOpen, setTaskModalOpen] = React.useState(false);
  const [selectedTask, setSelectedTask] = React.useState(null);

  const sprint = data?.sprint;
  const items = (sprint && sprint.items) ? sprint.items : [];
  const [localItems, setLocalItems] = React.useState(items);
  const currentUser = cuData?.getCurrentUser || null;
  const showPrompt = shouldShowAssessmentPrompt(currentUser);

  // Initialize local items when sprint changes to avoid update loops on shallow-changed arrays
  React.useEffect(() => {
    setLocalItems(items);
  }, [sprint?.id]);

  // Expose a debug helper to open the drawer from console
  React.useEffect(() => {
    try { window.openSprintDrawer = () => setDrawerOpen(true); } catch(_) {}
    return () => { try { delete window.openSprintDrawer; } catch(_) {} };
  }, []);
  // Backlog items = action plan items not already in sprint and with allowed statuses
  const backlogItems = React.useMemo(() => {
    const apItems = apData?.actionPlan?.items || [];
    const inSprint = new Set(items.map((i) => i.id));
    return apItems.filter((i) => !inSprint.has(i.id) && (i.status === 'open' || i.status === 'in_progress'));
  }, [apData, items]);

  if (!sprintId) {
    return (
      <SprintEmptyState
        backHref="/succession/sprint"
        onCreate={() => {
          const event = new CustomEvent('open-sprint-create');
          window.dispatchEvent(event);
        }}
      />
    );
  }
  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading sprint</div>;

  if (!sprint) {
    return (
      <SprintEmptyState
        backHref="/succession/sprint"
        onCreate={() => {
          const event = new CustomEvent('open-sprint-create');
          window.dispatchEvent(event);
        }}
      />
    );
  }
  const metrics = sprint?.metricsSnapshot || { completionPct: 0, totalItems: 0, completedItems: 0, overdueCount: 0, blockedCount: 0 };

  const handleItemUpdate = async (itemId, updates) => {
    // Optimistic local move
    let prev;
    setLocalItems((prevItems) => {
      prev = prevItems;
      if (updates.status) {
        return (prevItems || []).map((it) => (it.id === itemId ? { ...it, status: updates.status } : it));
      }
      return prevItems;
    });

    const vars = { id: itemId };
    if (updates.status) vars.status = String(updates.status).toUpperCase();
    if (updates.priority) vars.priority = String(updates.priority).toUpperCase();
    if (updates.dueAt) vars.dueAt = updates.dueAt;
    if (updates.assigneeUserId) vars.assigneeUserId = updates.assigneeUserId;
    if (updates.pipelineCardId) vars.pipelineCardId = updates.pipelineCardId;
    if (updates.title !== undefined) vars.title = updates.title;
    if (updates.description !== undefined) vars.description = updates.description;

    const existing = (localItems || items).find((it) => it.id === itemId) || {};
    const optimistic = {
      update_action_plan_item: {
        __typename: 'ActionPlanItem',
        id: itemId,
        status: updates.status || existing.status,
        priority: (updates.priority ? String(updates.priority).toUpperCase() : existing.priority) || 'MEDIUM',
        dueAt: updates.dueAt || existing.dueAt || null,
      },
    };

    try {
      await updateItem({ variables: vars, optimisticResponse: optimistic });
      refetch();
    } catch (e) {
      // Revert optimistic change on error
      if (prev) setLocalItems(prev);
      // Optional: surface error in UI
      // eslint-disable-next-line no-console
      console.error('Failed to update item', e);
    }
  };

  const handleAddFromBacklog = async (ids) => {
    await addItems({ variables: { sprint_id: sprint.id, item_ids: ids } });
    refetch();
  };

  return (
    <SingleColumnTemplate 
      title={sprint?.name || 'Sprint'} 
      variant="wide"
      breadcrumbs={
        <a 
          href="/sprints" 
          className="inline-flex items-center gap-2 text-sm text-succession-text-secondary hover:text-succession-text-primary transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>Back to Sprints</span>
        </a>
      }
    >
      <Box className="bg-succession-bg-primary border border-succession-border rounded-lg overflow-hidden">
        <SprintMetricsStripSimple metrics={metrics} items={items} sprint={sprint} />
        <div className="border-t border-succession-border px-6 py-3 flex items-center justify-between transition-theme">
          <h2 className="text-lg font-medium text-succession-text-primary">Tasks</h2>
          <button className="inline-flex items-center rounded-md bg-succession-primary px-3 py-2 text-sm font-medium text-white hover:bg-succession-primary/90 border border-succession-border shadow-sm" onClick={() => { setDrawerOpen(true); try { window.openSprintDrawer = () => setDrawerOpen(true); } catch(_) {} }}>
            <span className="mr-2">+</span> Add Tasks
          </button>
        </div>
      </Box>
      {showPrompt ? (
        <div className="p-6">
          <AssessmentPromptEmptyState placement="sprint" />
        </div>
      ) : (
        <>
          {/* Post-exit overlay onboarding: highlight backlog with guided tour if requested */}
          <div data-tutorial-id="sprint-backlog" />
          <SprintKanbanBoard
            items={localItems || items}
            onItemUpdate={handleItemUpdate}
            onItemClick={(it) => { setSelectedTask(it); setTaskModalOpen(true); }}
          />
          <div className="px-6 mt-6">
            <SprintCommentsSection
              sprintId={sprint.id}
              sprintName={sprint.name}
              currentUserId={currentUser?.id}
              defaultExpanded={false}
              commentCount={sprint.commentCount || 0}
              unreadCount={sprint.unreadCommentCount || 0}
            />
          </div>
        </>
      )}
      <AddFromBacklogDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        backlogItems={backlogItems}
        onAddItems={handleAddFromBacklog}
      />
      <TaskDetailModal
        isOpen={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        task={selectedTask}
        onSave={async (id, updates) => { await handleItemUpdate(id, updates); setTaskModalOpen(false); }}
        onMarkCompleted={async (id) => { await handleItemUpdate(id, { status: 'completed' }); setTaskModalOpen(false); }}
        onDelete={async (_id) => { setTaskModalOpen(false); }}
        onTaskUpdated={async () => {
          const result = await refetch();
          // Update selectedTask with fresh data after refetch
          if (selectedTask && result.data?.sprint?.items) {
            const updatedTask = result.data.sprint.items.find((item) => item.id === selectedTask.id);
            if (updatedTask) {
              setSelectedTask(updatedTask);
            }
          }
        }}
      />
    </SingleColumnTemplate>
  );
};

export default SprintDetailContainer;


